import cn from "classnames";
import { useAtomValue, useSetAtom } from "jotai";
import { type KeyboardEvent, useRef } from "react";
import { useTranslation } from "react-i18next";
import { canv } from "@/canv/global";
import { ArrowIcon } from "@/icons/widget-menu";
import { widgetMenuOptionsAtom, widgetMenuOptionsByNameAtom } from "@/interface/AppStore";
import { Tooltip } from "@/interface/ui/Tooltip";
import { VStack } from "@/shared/ui/Stack";
import { getFontSize } from "@/utils";
import { TEXT_SIZES } from "../../constants/textSizes";
import { TOOLTIP_BUTTON_CLASS } from "../../constants/tooltipButtonClass";
import { applyValue, type Direction, findNearestStep, setNewOptions } from "../../lib";
import type { ActiveSubMenuState, SetActiveSubMenu } from "../../types";
import styles from "./TextSizeButton.module.css";
import { TextSizeInput } from "./TextSizeInput";

interface TextSizeButtonProps {
    activeSubMenu: ActiveSubMenuState | null;
    setActiveSubMenu: SetActiveSubMenu;
}

export function TextSizeButton({ activeSubMenu, setActiveSubMenu }: TextSizeButtonProps) {
    const { t } = useTranslation();
    const wrapperRef = useRef<HTMLDivElement>(null);

    const options = useAtomValue(widgetMenuOptionsByNameAtom("textSize"));
    const setWidgetMenuOptions = useSetAtom(widgetMenuOptionsAtom);

    const uniqueValues = [...new Set(options.map((option) => option.value))];
    const isSubMenuActive = activeSubMenu?.name === "textSize";

    const handleInputFocus = () => {
        canv.appInterface.inputFocused = 1;
    };

    const handleInputBlur = () => {
        canv.appInterface.inputFocused = 0;
    };

    const handleButtonClick = () => {
        const anchor = wrapperRef.current;

        if (!anchor) {
            return;
        }

        setActiveSubMenu((prev) =>
            prev?.name === "textSize" ? null : { name: "textSize", anchor },
        );
    };

    const handleInputKeydown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Enter") {
            return;
        }
        const input = event.currentTarget;
        const value = getFontSize(Number.parseInt(input.value, 10));

        if (value) {
            const setNewTextSizeOptions = setNewOptions({ name: "textSize", setWidgetMenuOptions });

            function handleClose() {
                setActiveSubMenu(null);
            }

            applyValue({ options, value, setNewOptions: setNewTextSizeOptions, handleClose });
        }
    };

    const handleArrowButtonClick = (direction: Direction) => {
        const value = findNearestStep(uniqueValues[0] as number, direction);

        if (value) {
            const setNewTextSizeOptions = setNewOptions({ name: "textSize", setWidgetMenuOptions });

            function handleClose() {
                setActiveSubMenu(null);
            }

            applyValue({ options, value, setNewOptions: setNewTextSizeOptions, handleClose });
        }
    };

    const isArrowUpDisabled = (uniqueValues[0] as number) >= TEXT_SIZES[TEXT_SIZES.length - 1];
    const isArrowDownDisabled = (uniqueValues[0] as number) <= TEXT_SIZES[0];

    const arrowUpStyles = cn(isArrowUpDisabled && styles.disabledArrow);

    const arrowDownStyles = cn(styles.arrowDown, isArrowDownDisabled && styles.disabledArrow);

    const fontSizeValue = Math.trunc(uniqueValues[0] as number);

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            {isSubMenuActive ? (
                <TextSizeInput
                    fontSize={fontSizeValue}
                    onInputFocus={handleInputFocus}
                    onInputBlur={handleInputBlur}
                    onInputKeydown={handleInputKeydown}
                />
            ) : (
                <button
                    type="button"
                    className={`${styles.textButton} ${TOOLTIP_BUTTON_CLASS}`}
                    onClick={handleButtonClick}
                    data-tooltip={t("widgetMenu.fontSize")}
                >
                    {fontSizeValue}
                </button>
            )}

            <VStack gap={4}>
                <button
                    className={`${styles.iconButton} ${TOOLTIP_BUTTON_CLASS}`}
                    type="button"
                    disabled={isArrowUpDisabled}
                    onClick={() => handleArrowButtonClick("up")}
                    data-tooltip={t("widgetMenu.increase")}
                >
                    <ArrowIcon className={arrowUpStyles} />
                </button>

                <Tooltip content={t("widgetMenu.decrease")} placement="bottom" offset={[0, 12]}>
                    <button
                        className={styles.iconButton}
                        type="button"
                        disabled={isArrowDownDisabled}
                        onClick={() => handleArrowButtonClick("down")}
                    >
                        <ArrowIcon className={arrowDownStyles} />
                    </button>
                </Tooltip>
            </VStack>
        </div>
    );
}
